import { Head, router } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { useDebouncedSearch } from "@/hooks/use-debounced-search"
import { Badge } from "@/components/ui/badge"
import { Search, Upload, Loader2, CheckCircle2, XCircle, Minus, MapPin } from "lucide-react"
import { useRef } from "react"
import type { Commune, PagyMetadata, PluExtractionStatus } from "@/types"

const pluStatusConfig: Record<
  PluExtractionStatus,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ElementType }
> = {
  none: { label: "Non extrait", variant: "outline", icon: Minus },
  in_progress: { label: "En cours", variant: "secondary", icon: Loader2 },
  completed: { label: "Extrait", variant: "default", icon: CheckCircle2 },
  failed: { label: "Échoué", variant: "destructive", icon: XCircle },
}

type Props = {
  communes: Commune[]
  filters: Record<string, string>
  pagy: PagyMetadata
}

function CommunesIndex({ communes, filters, pagy }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [search, setSearch, searchRef] = useDebouncedSearch(
    filters.name_or_insee_code_cont || "",
    "/communes",
    "name_or_insee_code_cont",
  )

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    router.post("/communes/import", { file }, { forceFormData: true })
    e.target.value = ""
  }

  return (
    <>
      <Head title="Communes" />
      <PageHeader title="Communes" />
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchRef}
              placeholder="Rechercher par nom ou code INSEE..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
            </Badge>
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleImport} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="size-4" />
              Importer CSV
            </Button>
          </div>
        </div>

        <Card>
          <CardContent className="px-0">
            {communes.length === 0 ? (
              <EmptyState
                icon={MapPin}
                title="Aucune commune trouvée"
                description="Essayez de modifier vos critères de recherche."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code INSEE</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Code postal</TableHead>
                    <TableHead>Département</TableHead>
                    <TableHead>Région</TableHead>
                    <TableHead>Extraction PLU</TableHead>
                    <TableHead className="text-right">Population</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {communes.map((commune) => (
                    <TableRow
                      key={commune.id}
                      className="cursor-pointer"
                      onClick={() => router.visit(`/communes/${commune.id}`)}
                    >
                      <TableCell className="font-mono">{commune.insee_code}</TableCell>
                      <TableCell className="font-medium">{commune.name}</TableCell>
                      <TableCell className="max-w-40 truncate">{commune.postal_codes || "—"}</TableCell>
                      <TableCell>
                        {commune.department_name ? `${commune.department_code} - ${commune.department_name}` : "—"}
                      </TableCell>
                      <TableCell>{commune.region_name || "—"}</TableCell>
                      <TableCell>
                        {(() => {
                          const config = pluStatusConfig[commune.plu_extraction_status]
                          const Icon = config.icon
                          const run = commune.last_plu_extraction_run
                          return (
                            <div className="flex flex-col gap-1">
                              <Badge variant={config.variant}>
                                <Icon
                                  className={commune.plu_extraction_status === "in_progress" ? "animate-spin" : ""}
                                />
                                {config.label}
                              </Badge>
                              {run && run.matched_zone_count != null && (
                                <span className="text-muted-foreground text-xs">
                                  {run.matched_zone_count} zone{run.matched_zone_count > 1 ? "s" : ""} · H{" "}
                                  {run.high_confidence_counts.hauteur} · E {run.high_confidence_counts.etages} · ES{" "}
                                  {run.high_confidence_counts.emprise}
                                </span>
                              )}
                            </div>
                          )
                        })()}
                      </TableCell>
                      <TableCell className="text-right">{commune.population?.toLocaleString("fr-FR") || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          {pagy.pages > 1 && (
            <CardFooter>
              <Pagination pagy={pagy} baseUrl="/communes" filters={filters} className="w-full p-0" />
            </CardFooter>
          )}
        </Card>
      </div>
    </>
  )
}

CommunesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default CommunesIndex
